// File path: /core/frontend/src/context/SubscriptionContext.tsx
// Subscription Context - Manages module access based on organization subscription

import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import subscriptionService from '../services/subscriptionService';
import { useAuth } from './AuthContext';
import {
  ModuleId,
  ModuleTier,
  ModuleSubscription,
  MODULE_REGISTRY,
  tierIncludes,
  getFeatureTier
} from '../config/moduleRegistry';

// Context type definition
export interface SubscriptionContextType {
  // State
  isLoading: boolean;
  error: string | null;
  modules: ModuleSubscription[];

  // Module access methods
  hasModuleAccess: (moduleId: ModuleId) => boolean;
  hasFeatureAccess: (moduleId: ModuleId, featureId: string) => boolean;
  getModuleTier: (moduleId: ModuleId) => ModuleTier | undefined;
  getEnabledFeatures: (moduleId: ModuleId) => string[];

  // Get accessible modules for navigation
  getAccessibleModules: () => ModuleSubscription[];

  // Refresh subscription data
  refreshSubscription: () => Promise<void>;
}

// Default context value
const defaultContext: SubscriptionContextType = {
  isLoading: true,
  error: null,
  modules: [],
  hasModuleAccess: () => false,
  hasFeatureAccess: () => false,
  getModuleTier: () => undefined,
  getEnabledFeatures: () => [],
  getAccessibleModules: () => [],
  refreshSubscription: async () => {},
};

// Create context
const SubscriptionContext = createContext<SubscriptionContextType>(defaultContext);

/**
 * enabled_features as a list of feature ids. The API sends a JSON array, or — a TEXT[] column read
 * through db_helper — the RDS Data API's {stringValues: [...]}. Anything else counts as no list
 * (all features of the tier), never as a string to search.
 */
export function toFeatureList(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  if (value && typeof value === 'object' && Array.isArray((value as { stringValues?: unknown }).stringValues)) {
    return toFeatureList((value as { stringValues: unknown[] }).stringValues);
  }
  return [];
}

// Hook to use the subscription context
export const useSubscription = () => useContext(SubscriptionContext);

// Provider props
interface SubscriptionProviderProps {
  children: React.ReactNode;
}

// Provider component
export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [modules, setModules] = useState<ModuleSubscription[]>([]);

  // Load the user's modules: exactly what GET /subscriptions/user returns (Task 2, S4 Ruling 46).
  // A failed call grants nothing and records the error — no default modules are substituted
  // (the C-503UI principle: show the failure, never stand-in data). The server enforces the same
  // subscriptions (the authorizer's MODULE_NOT_SUBSCRIBED).
  // Only the latest load (or clear) may write state: a response for a signed-out or replaced
  // user arrives too late to matter.
  const latestRequest = useRef(0);

  const loadUserModules = useCallback(async () => {
    const request = ++latestRequest.current;
    const isCurrent = () => request === latestRequest.current;
    try {
      setIsLoading(true);
      setError(null);

      const response = await subscriptionService.getUserModules();
      if (!isCurrent()) return;

      // Handle both snake_case (from API) and camelCase (if already transformed)
      const userModules: ModuleSubscription[] = (response.modules || []).map((m: any) => ({
        moduleId: (m.module_id || m.moduleId) as ModuleId,
        moduleName: m.module_name || m.moduleName || 'Unknown',
        tier: (m.tier || 'basic') as ModuleTier,
        enabledFeatures: toFeatureList(m.enabled_features ?? m.enabledFeatures),
      }));

      // core: the API lists it for every user; keep it if a response ever omits it.
      if (!userModules.some(m => m.moduleId === 'core')) {
        userModules.unshift({ moduleId: 'core', moduleName: 'Core', tier: 'basic', enabledFeatures: [] });
      }

      setModules(userModules);
    } catch (err: any) {
      if (!isCurrent()) return;
      console.error('Error loading user modules:', err);
      setModules([]);
      setError(`Could not load your modules${err?.message ? `: ${err.message}` : '.'}`);
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, []);

  // Signed out: no modules, no error, nothing loading.
  const clearModules = useCallback(() => {
    latestRequest.current += 1;
    setModules([]);
    setError(null);
    setIsLoading(false);
  }, []);

  // Follow the auth state (this provider sits inside AuthProvider in the shell and in Elevate's
  // App): load once auth has decided the user is signed in, reload when the signed-in user
  // changes (dropping the previous user's modules first), clear on sign-out. This covers a
  // same-tab sign-in, sign-out or user switch, and an expired session at mount (auth refreshes
  // or signs out before anything is fetched). RoleBasedRedirect's one refresh stays a backstop.
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const userId = user?.id ?? null;
  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated) {
      setModules([]);
      loadUserModules();
    } else {
      clearModules();
    }
  }, [authLoading, isAuthenticated, userId, loadUserModules, clearModules]);

  // Another tab signed in or out (the storage event fires in other tabs only)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'accessToken') {
        if (e.newValue) {
          loadUserModules();
        } else {
          clearModules();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadUserModules, clearModules]);

  // Check if user has access to a module
  const hasModuleAccess = useCallback((moduleId: ModuleId): boolean => {
    // Core is always accessible
    if (moduleId === 'core') return true;

    // Nothing else is granted while the subscription call is in flight
    if (isLoading) return false;

    // Check if module is in user's subscription
    return modules.some(m => m.moduleId === moduleId);
  }, [modules, isLoading]);

  // Check if user has access to a specific feature within a module
  const hasFeatureAccess = useCallback((moduleId: ModuleId, featureId: string): boolean => {
    // First check module access
    if (!hasModuleAccess(moduleId)) return false;

    const userModule = modules.find(m => m.moduleId === moduleId);
    if (!userModule) return false;

    // Get required tier for this feature
    const requiredTier = getFeatureTier(moduleId, featureId);
    if (!requiredTier) {
      // Feature not defined, assume accessible
      return true;
    }

    // Check if user's tier includes the required tier
    if (!tierIncludes(userModule.tier, requiredTier)) {
      return false;
    }

    // If enabled_features is empty, all features for the tier are enabled
    if (userModule.enabledFeatures.length === 0) {
      return true;
    }

    // Check if specific feature is enabled
    return userModule.enabledFeatures.includes(featureId);
  }, [modules, hasModuleAccess]);

  // Get the tier for a module the user has access to
  const getModuleTier = useCallback((moduleId: ModuleId): ModuleTier | undefined => {
    const userModule = modules.find(m => m.moduleId === moduleId);
    return userModule?.tier;
  }, [modules]);

  // Get enabled features for a module
  const getEnabledFeatures = useCallback((moduleId: ModuleId): string[] => {
    const userModule = modules.find(m => m.moduleId === moduleId);
    if (!userModule) return [];

    // If no specific features defined, return all features for the tier
    if (userModule.enabledFeatures.length === 0) {
      const moduleDefinition = MODULE_REGISTRY[moduleId];
      if (!moduleDefinition) return [];

      return moduleDefinition.features
        .filter(f => tierIncludes(userModule.tier, f.tier))
        .map(f => f.id);
    }

    return userModule.enabledFeatures;
  }, [modules]);

  // Get all accessible modules (for navigation)
  const getAccessibleModules = useCallback((): ModuleSubscription[] => {
    return modules.filter(m => m.moduleId !== 'core');
  }, [modules]);

  // Refresh subscription data
  const refreshSubscription = useCallback(async (): Promise<void> => {
    await loadUserModules();
  }, [loadUserModules]);

  // Context value
  const value: SubscriptionContextType = {
    isLoading,
    error,
    modules,
    hasModuleAccess,
    hasFeatureAccess,
    getModuleTier,
    getEnabledFeatures,
    getAccessibleModules,
    refreshSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Higher-order component for module access protection
export function withModuleAccess<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredModuleId: ModuleId,
  FallbackComponent?: React.ComponentType
) {
  return function ModuleProtectedComponent(props: P) {
    const { hasModuleAccess, isLoading } = useSubscription();

    if (isLoading) {
      return <div className="flex items-center justify-center p-8">Loading...</div>;
    }

    if (!hasModuleAccess(requiredModuleId)) {
      if (FallbackComponent) {
        return <FallbackComponent />;
      }
      return (
        <div className="flex flex-col items-center justify-center p-8">
          <h2 className="text-xl font-semibold text-gray-700">Module Not Available</h2>
          <p className="text-gray-500 mt-2">
            Your organization does not have access to this module.
          </p>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
}

// Higher-order component for feature access protection
export function withFeatureAccess<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredModuleId: ModuleId,
  requiredFeatureId: string,
  FallbackComponent?: React.ComponentType
) {
  return function FeatureProtectedComponent(props: P) {
    const { hasFeatureAccess, isLoading } = useSubscription();

    if (isLoading) {
      return <div className="flex items-center justify-center p-8">Loading...</div>;
    }

    if (!hasFeatureAccess(requiredModuleId, requiredFeatureId)) {
      if (FallbackComponent) {
        return <FallbackComponent />;
      }
      return (
        <div className="flex flex-col items-center justify-center p-8">
          <h2 className="text-xl font-semibold text-gray-700">Feature Not Available</h2>
          <p className="text-gray-500 mt-2">
            This feature requires an upgraded subscription tier.
          </p>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
}

export default SubscriptionContext;
